// Geolocation behind a tiny provider interface.
//
// BROWSER FIRST. This is a web app that Tauri only wraps for export, so the
// default provider is always the plain browser one and nothing in
// apps/web statically imports any @tauri-apps package. Those packages live
// in apps/mobile and Bun workspaces do not hoist them, so a static import
// would fail at BUILD time, which is unrecoverable. The native upgrade is
// therefore detected at runtime and loaded through a dynamic import inside a
// try/catch: if it is not there, or it throws, the browser provider stays.
//
// Components never call navigator.geolocation directly either, so a
// different provider can be installed with setGeoProvider() without
// touching a single screen.

export interface GeoFix {
  lat: number;
  lng: number;
  accuracyM: number | null;
  altitudeM: number | null;
  speedMps: number | null;
  headingDeg: number | null;
  recordedAt: string;
}

export type GeoErrorKind = "denied" | "unavailable" | "timeout";

export interface GeoError {
  kind: GeoErrorKind;
  message: string;
}

export interface GeoWatchHandle {
  stop(): void;
}

export interface GeoProvider {
  isAvailable(): boolean;
  getCurrent(timeoutMs?: number): Promise<GeoFix>;
  watch(onFix: (fix: GeoFix) => void, onError: (error: GeoError) => void): GeoWatchHandle;
}

function toFix(position: GeolocationPosition): GeoFix {
  const c = position.coords;
  return {
    lat: c.latitude,
    lng: c.longitude,
    accuracyM: Number.isFinite(c.accuracy) ? c.accuracy : null,
    altitudeM: c.altitude ?? null,
    speedMps: c.speed ?? null,
    headingDeg: c.heading ?? null,
    recordedAt: new Date(position.timestamp || Date.now()).toISOString(),
  };
}

function toError(error: GeolocationPositionError): GeoError {
  if (error.code === 1) return { kind: "denied", message: error.message };
  if (error.code === 3) return { kind: "timeout", message: error.message };
  return { kind: "unavailable", message: error.message };
}

/** Browser and WebView provider. High accuracy on, per the product spec. */
export const browserGeoProvider: GeoProvider = {
  isAvailable() {
    // The property can EXIST and still be null: happy-dom does that, and so
    // do some locked down WebViews. Check the value, not the key.
    return typeof navigator !== "undefined" && navigator.geolocation != null;
  },
  getCurrent(timeoutMs = 15000) {
    return new Promise<GeoFix>((resolve, reject) => {
      if (!browserGeoProvider.isAvailable()) {
        reject({ kind: "unavailable", message: "geolocation not supported" } satisfies GeoError);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve(toFix(position)),
        (error) => reject(toError(error)),
        { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
      );
    });
  },
  watch(onFix, onError) {
    if (!browserGeoProvider.isAvailable()) {
      onError({ kind: "unavailable", message: "geolocation not supported" });
      return { stop() {} };
    }
    const id = navigator.geolocation.watchPosition(
      (position) => onFix(toFix(position)),
      (error) => onError(toError(error)),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 1000 },
    );
    return {
      stop() {
        navigator.geolocation.clearWatch(id);
      },
    };
  },
};

let active: GeoProvider = browserGeoProvider;

export function setGeoProvider(provider: GeoProvider): void {
  active = provider;
}

export function getGeoProvider(): GeoProvider {
  return active;
}

function isTauriRuntime(): boolean {
  const host = globalThis as Record<string, unknown>;
  return Boolean(host["__TAURI_INTERNALS__"] ?? host["__TAURI__"]);
}

/**
 * Opportunistically upgrade to the Tauri geolocation plugin when the app is
 * running inside the native shell. Never throws, never blocks boot, and is a
 * no-op in a plain browser. Call it once at startup.
 */
export async function upgradeGeoProviderIfNative(): Promise<boolean> {
  if (!isTauriRuntime()) return false;
  try {
    // Specifier built at runtime so the bundler cannot try to resolve a
    // package that only exists in apps/mobile.
    const specifier = ["@tauri-apps", "plugin-geolocation"].join("/");
    const plugin = (await import(/* @vite-ignore */ specifier)) as {
      getCurrentPosition?(options?: unknown): Promise<GeolocationPosition>;
      watchPosition?(
        options: unknown,
        callback: (position: GeolocationPosition | null) => void,
      ): Promise<number>;
      clearWatch?(id: number): Promise<void>;
    };
    if (!plugin.getCurrentPosition || !plugin.watchPosition) return false;

    const options = { enableHighAccuracy: true, timeout: 20000, maximumAge: 1000 };
    setGeoProvider({
      isAvailable: () => true,
      async getCurrent() {
        return toFix(await plugin.getCurrentPosition!(options));
      },
      watch(onFix, onError) {
        let watchId: number | null = null;
        void plugin
          .watchPosition!(options, (position) => {
            if (position) onFix(toFix(position));
          })
          .then((id) => {
            watchId = id;
          })
          .catch((error: unknown) =>
            onError({ kind: "unavailable", message: String(error) }),
          );
        return {
          stop() {
            if (watchId !== null) void plugin.clearWatch?.(watchId);
          },
        };
      },
    });
    return true;
  } catch {
    // Plugin missing or failed to initialise: stay on the browser provider.
    return false;
  }
}

/** Great circle distance in metres between two fixes. */
export function distanceM(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
